import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useTriggerFormInit,
  useNodeDropdownLabels,
  buildShipmentFilterUpdate,
} from '../shared';
import { Form, Flex, Checkbox } from 'antd';
import useToken from '@/common/helper/useToken';
import { usePaginatedFilters } from '@/common/helper/usePaginatedFilters';
import { ALERT_TRIGGER_FILTER_CONFIGS } from '@/common/helper/filterConfigs';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { getApplyConfig } from '@/pages/Settings/Notifications/NotificationConfig';
import { useFetchCompanyCouriers } from '@/hooks';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { type DropdownLabelItem } from '@/apis/logiblocksLabelsAPI';
import { ShipmentFilterApply } from '../alertTrigger/components';
import { CustomShipmentText } from '@/components';

import { ShipmentFilterFormData } from '../nodeFormTypes';

interface StatusProgressTriggerFormData extends ShipmentFilterFormData {
  is_pickup_confirmation?: boolean;
  is_delivery_confirmation?: boolean;
  is_delivery_exception?: boolean;
  is_created?: boolean;
  is_out_for_delivery?: boolean;
  is_incomplete?: boolean;
}

/**
 * Customization UI for STATUS_PROGRESS_TRIGGER nodes.
 */
export const StatusProgressTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isSidePannelOpen,
  nodeId,
  workflowId,
  isReadOnly = false,
}) => {
  const [form] = Form.useForm();
  const { getToken, decodedUserToken } = useToken();
  const isWorldCourierCompany = Boolean(decodedUserToken()?.enablecustomerleg);
  const { couriers } = useFetchCompanyCouriers();
  const [teamsList, setTeamsList] = useState([]);
  const [applyType, setApplyType] = useState([]);

  // Pre-loaded labels for paginated dropdowns (fetched once when panel opens).
  const preloadedLabels = useNodeDropdownLabels({
    isSidePannelOpen,
    workflowId,
    nodeId,
    getToken,
  });

  // --- Teams -------------------------------------------------------------

  useEffect(() => {
    getTeamsDDLOFFeature('', 'Shipment').then((val) => {
      if (val && val.length > 0) setTeamsList(val);
    });
  }, []);

  // --- Paginated filters -------------------------------------------------

  const {
    getFilterData,
    getFilterLoading,
    getFilterHasMore,
    getFilterLoadMore,
  } = usePaginatedFilters(ALERT_TRIGGER_FILTER_CONFIGS, getToken);

  // --- Merge helper ------------------------------------------------------

  const mergeWithPreloaded = useCallback(
    (pageData: unknown[], preloaded: DropdownLabelItem[]): unknown[] => {
      if (preloaded.length === 0) return pageData;
      const pageIds = new Set(
        (pageData as Array<{ id?: string | number }>).map((item) =>
          String(item.id ?? '')
        )
      );
      const extra = preloaded.filter((item) => !pageIds.has(String(item.id)));
      return [...pageData, ...extra];
    },
    []
  );

  const memoizedOriginsData = useMemo(
    () => mergeWithPreloaded(getFilterData(3), preloadedLabels.origins),
    [getFilterData, mergeWithPreloaded, preloadedLabels.origins]
  );
  const memoizedProductsData = useMemo(
    () => mergeWithPreloaded(getFilterData(1), preloadedLabels.products),
    [getFilterData, mergeWithPreloaded, preloadedLabels.products]
  );
  const memoizedDestinationsData = useMemo(
    () => mergeWithPreloaded(getFilterData(4), preloadedLabels.destinations),
    [getFilterData, mergeWithPreloaded, preloadedLabels.destinations]
  );

  const applyConfig = useMemo(
    () =>
      getApplyConfig({
        teamsList,
        origins: memoizedOriginsData,
        products: memoizedProductsData,
        couriers,
        destinations: memoizedDestinationsData,
      }),
    [
      teamsList,
      memoizedOriginsData,
      memoizedProductsData,
      couriers,
      memoizedDestinationsData,
    ]
  );

  // --- Node update handler -----------------------------------------------

  const handleNodeData = (
    data: StatusProgressTriggerFormData,
    isSelectAll?: boolean
  ) => {
    if (!data || Object.keys(data).length === 0) return;
    onChange({
      status_progress_trigger: buildShipmentFilterUpdate(
        data,
        nodeData.status_progress_trigger,
        applyType,
        {
          is_pickup_confirmation: data.is_pickup_confirmation ?? false,
          is_delivery_confirmation: data.is_delivery_confirmation ?? false,
          is_delivery_exception: data.is_delivery_exception ?? false,
          is_created: data.is_created ?? false,
          is_out_for_delivery: data.is_out_for_delivery ?? false,
          is_incomplete: data.is_incomplete ?? false,
        },
        isSelectAll
      ),
    });
  };

  // --- Form initialization -----------------------------------------------

  const hasPatchedRef = useTriggerFormInit({
    isSidePannelOpen,
    triggerData: nodeData?.status_progress_trigger,
    form,
    setApplyType,
    extraFormValues: {
      is_pickup_confirmation:
        nodeData?.status_progress_trigger?.is_pickup_confirmation ?? false,
      is_delivery_confirmation:
        nodeData?.status_progress_trigger?.is_delivery_confirmation ?? false,
      is_delivery_exception:
        nodeData?.status_progress_trigger?.is_delivery_exception ?? false,
      is_created: nodeData?.status_progress_trigger?.is_created ?? false,
      is_out_for_delivery:
        nodeData?.status_progress_trigger?.is_out_for_delivery ?? false,
      is_incomplete: nodeData?.status_progress_trigger?.is_incomplete ?? false,
    },
  });

  // Reset the form-init patch flag on panel close so re-opening re-initializes.
  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen, hasPatchedRef]);

  return (
    <Form
      form={form}
      layout='vertical'
      onValuesChange={(_changed, all) => handleNodeData(all)}
    >
      {' '}
      <Flex vertical gap={16}>
        <Flex vertical gap={4}>
          <CustomShipmentText
            text={'Shipment status change'}
            fontSize='14px'
            fontWeight={600}
            marginBottom='10px'
          />
          <Form.Item shouldUpdate name='confirmation'>
            <Flex vertical gap={8}>
              <Form.Item name='is_created' valuePropName='checked' noStyle>
                <Checkbox
                  className='notification-checkbox'
                  disabled={isReadOnly}
                >
                  <span className='notification-checkbox-label'>Created</span>
                </Checkbox>
              </Form.Item>
              <Form.Item
                name='is_pickup_confirmation'
                valuePropName='checked'
                noStyle
              >
                <Checkbox
                  className='notification-checkbox'
                  disabled={isReadOnly}
                >
                  <span className='notification-checkbox-label'>
                    In transit (picked up)
                  </span>
                </Checkbox>
              </Form.Item>
              {isWorldCourierCompany && (
                <Form.Item name='is_incomplete' valuePropName='checked' noStyle>
                  <Checkbox
                    className='notification-checkbox'
                    disabled={isReadOnly}
                  >
                    <span className='notification-checkbox-label'>
                      Incomplete
                    </span>
                  </Checkbox>
                </Form.Item>
              )}
              {!isWorldCourierCompany && (
                <>
                  <Form.Item
                    name='is_delivery_exception'
                    valuePropName='checked'
                    noStyle
                  >
                    <Checkbox
                      className='notification-checkbox'
                      disabled={isReadOnly}
                    >
                      <span className='notification-checkbox-label'>
                        Delivery exception
                      </span>
                    </Checkbox>
                  </Form.Item>
                  <Form.Item
                    name='is_out_for_delivery'
                    valuePropName='checked'
                    noStyle
                  >
                    <Checkbox
                      className='notification-checkbox'
                      disabled={isReadOnly}
                    >
                      <span className='notification-checkbox-label'>
                        Out for delivery
                      </span>
                    </Checkbox>
                  </Form.Item>
                </>
              )}

              <Form.Item
                name='is_delivery_confirmation'
                valuePropName='checked'
                noStyle
              >
                <Checkbox
                  className='notification-checkbox'
                  disabled={isReadOnly}
                >
                  <span className='notification-checkbox-label'>Delivered</span>
                </Checkbox>
              </Form.Item>
            </Flex>
          </Form.Item>
        </Flex>
        <ShipmentFilterApply
          applyType={applyType}
          setApplyType={setApplyType}
          form={form}
          applyConfig={applyConfig}
          handleNodeUpdate={handleNodeData}
          isReadOnly={isReadOnly}
          origins={memoizedOriginsData}
          originsLoading={getFilterLoading(3)}
          originsHasMore={getFilterHasMore(3)}
          onLoadMoreOrigins={getFilterLoadMore(3)}
          products={memoizedProductsData}
          productsLoading={getFilterLoading(1)}
          productsHasMore={getFilterHasMore(1)}
          onLoadMoreProducts={getFilterLoadMore(1)}
          destinations={memoizedDestinationsData}
          destinationsLoading={getFilterLoading(4)}
          destinationsHasMore={getFilterHasMore(4)}
          onLoadMoreDestinations={getFilterLoadMore(4)}
        />
      </Flex>
    </Form>
  );
};
